import brandService from '../../../services/brandService';
import filterService from '../../../services/filterService';

import {
    setBrandLoading,
    setBrandError,
    setBrandList,
    setBrandCurrent,
    setBrandPagination,
    setBrandSort,
    setBrandFilters,
    setBrandDefaultFilter,
    setBrandSettings,
    clearBrandCurrent,
    clearBrandError,
    setBrandFilterLoading,
    changeBrandFilterCondition,
    addBrandFilterCondition,
    updateBrandFilterConditionRule,
    updateBrandFilterConditionValue,
    removeBrandFilterCondition,
    resetBrandFiltersAndPagination
} from './brandSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().brand.isSettingsInitialized) {
            dispatch(setBrandLoading(true));
            const response = await brandService.getSettings();

            // Set the settings
            dispatch(setBrandSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setBrandDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setBrandError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setBrandLoading(false));
    }
};

export const fetchBrands = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().brand;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setBrandLoading(true));
        const response = await brandService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setBrandList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchBrands:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setBrandError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setBrandLoading(false));
    }
};

export const fetchBrand = (id) => async (dispatch) => {
    try {
        dispatch(setBrandLoading(true));
        const response = await brandService.getOne(id);
        dispatch(setBrandCurrent(response.data));
    } catch (error) {
        dispatch(setBrandError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setBrandLoading(false));
    }
};

export const createBrand = (data) => async (dispatch) => {
    try {
        const response = await brandService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateBrand = (id, data) => async (dispatch) => {
    try {
        const response = await brandService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteBrand = (id) => async (dispatch) => {
    try {
        dispatch(setBrandLoading(true));
        const response = await brandService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setBrandError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setBrandLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setBrandPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setBrandSort(sort));
};

export const searchBrands = (query) => (dispatch, getState) => {
    const currentFilters = getState().brand.filters;
    dispatch(setBrandFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setBrandDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeBrandFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addBrandFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateBrandFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateBrandFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeBrandFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setBrandFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().brand.settings;
            dispatch(setBrandSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setBrandFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setBrandFilterLoading(false));
        dispatch(setBrandError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetBrandFiltersAndPagination());
};